import React from "react";
import { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";

import { MemoizedRecordsPanel } from "./recordsPanel/RecordsPanel";
import { MemoizedInfoPanel } from "./infoPanel/InfoPanel";
import { ParsedRecord } from "../../types/airtable";
import { RootState } from "../../store";

import "./SidePanel.scss";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.background.default
    }
  })
);

export default function SidePanel({
  records
}: {
  records: ParsedRecord[];
}): JSX.Element {
  const [infoPanelOpen, setInfoPanelOpen] = useState(false);
  const [infoRecord, setInfoRecord] = useState(records[0]);
  const [mouseOver, setMouseOver] = useState(false);
  const classes = useStyles();

  const zoomedPeak = useSelector(
    (state: RootState) => state.selection.selection.zoomedPeak
  );

  useEffect(() => {
    if (zoomedPeak) {
      const record = records.find((r) => r.PEAK_ID === zoomedPeak);
      if (record) {
        handleSelectRecord(record);
      }
    } else {
      handleCloseInfo();
    }
  }, [zoomedPeak]);

  const handleKeyDown = (event: KeyboardEvent) => {
    if (!mouseOver) return;
    switch (event.key) {
      case "ArrowLeft":
        handleCycleRecord("previous");
        break;
      case "ArrowRight":
        handleCycleRecord("next");
        break;
      case "Escape":
        handleCloseInfo();
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  });

  const handleSelectRecord = useCallback((record: ParsedRecord) => {
    setInfoRecord(record);
    setInfoPanelOpen(true);
  }, []);

  const handleCycleRecord = (direction: "previous" | "next") => {
    const curIndex = records.indexOf(infoRecord);
    switch (curIndex) {
      case 0:
        setInfoRecord(
          records[direction === "previous" ? records.length - 1 : 1]
        );
        break;
      case records.length - 1:
        setInfoRecord(
          records[direction === "previous" ? records.length - 2 : 0]
        );
        break;
      default:
        setInfoRecord(
          records[direction === "previous" ? curIndex - 1 : curIndex + 1]
        );
    }
  };

  const handleCloseInfo = () => {
    setInfoPanelOpen(false);
  };
  return (
    <div
      id="side-panel"
      className={classes.root}
      onMouseEnter={() => setMouseOver(true)}
      onMouseLeave={() => setMouseOver(false)}
    >
      {infoPanelOpen && (
        <MemoizedInfoPanel
          record={infoRecord}
          cycleRecord={handleCycleRecord}
          closeInfo={handleCloseInfo}
        />
      )}
      <MemoizedRecordsPanel
        records={records}
        handleSelectRecord={handleSelectRecord}
      />
    </div>
  );
}
